import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check } from "lucide-react";
import React, { useState } from "react";
import {
  TbAlertCircle
} from "react-icons/tb";

import { useAuth } from "@/context/AuthContext";
import { useGlobalLoading } from "@/context/GlobalLoadingContext";
import { PRIORITY_MAP } from "@/lib/priorityMap";
import { toast } from 'sonner';

export function PrioritySelect({ task, showLabel = false, iconOnly = false, onPriorityChange, onTaskChange, onRefreshActivities, className }) {
  const [open, setOpen] = useState(false);
  const { axiosInstance } = useAuth();
  const { showLoading, hideLoading } = useGlobalLoading();

  const handlePriorityChange = async (newPriority) => {
    // 1. Capture old value for rollback
    const oldPriority = task.priority;
    
    // 2. Optimistically update UI immediately
    const optimisticTask = {
      ...task,
      priority: newPriority
    };
    
    if (onTaskChange) {
      onTaskChange(optimisticTask);
    }
    
    if (onPriorityChange) {
      onPriorityChange(newPriority);
    }
    
    // 3. Fire API call (don't await before UI update)
    try {
      showLoading();
      await axiosInstance.patch(`/tasks/${task.organization.slug}/${task.key}`, { 
        priority: newPriority 
      });
      
      // 4. Refresh just activities (new activity was created)
      if (onRefreshActivities) {
        await onRefreshActivities();
      }
    } catch (error) {
      // 5. Rollback on error
      console.error('Error updating priority:', error);
      if (onTaskChange) {
        onTaskChange({ ...task, priority: oldPriority });
      }
      toast({
        title: "Error",
        description: "Failed to update priority",
        variant: "destructive"
      });
    } finally {
      hideLoading();
    }
  };

  return (
    <div className={className} onClick={(e) => e.stopPropagation()}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className={`${!iconOnly ? 'h-7 px-1.5 -ml-1.5 flex items-center gap-2' : 'h-4 w-4 p-0 rounded-full bg-none hover:opacity-50'}`}>
              {React.createElement(PRIORITY_MAP[task.priority].icon, { className: `w-4 h-4 ${PRIORITY_MAP[task.priority].color}` })}
              {!iconOnly && PRIORITY_MAP[task.priority].name}
            </Button>
            {showLabel}
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0" side="top" align="start">
          <Command>
            <CommandInput placeholder="Change priority..." />
            <CommandList>
              <CommandEmpty>No priority found.</CommandEmpty>
              <CommandGroup>
                {Object.entries(PRIORITY_MAP).map(([key, value]) => (
                  <CommandItem
                    key={key}
                    value={key}
                    onSelect={() => {
                      handlePriorityChange(key);
                      setOpen(false);
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" className="h-7 px-1.5 flex items-center gap-2">
                        {React.createElement(value.icon, { className: `w-4 h-4 ${value.color}` })}
                        {value.name}
                      </Button>
                    </div>
                    {task.priority === key && (
                      <Check className="ml-auto h-4 w-4" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
} 